import wave from '@/static/assets/policy_two/wave.png';
import { MEDIA_QUERIES } from '@lemonade-hq/nuis-atomic';
import parse from 'html-react-parser';
import { motion } from 'motion/react';
import Image from 'next/image';
import React from 'react';
import styled from 'styled-components';
import { MainHeader, Section, SectionParagraph } from '../shared/Content';
import JoinCTA from '../shared/JoinCTA';
// images
import coffeeImage from '@/static/assets/policy_two/coffee.png';
import githubLogo from '@/static/assets/policy_two/github_logo.png';
import iphoneImage from '@/static/assets/policy_two/iphone.png';
import laptopImage from '@/static/assets/policy_two/laptop.png';
import plantImage from '@/static/assets/policy_two/plant.png';
import { font } from '@lemonade-hq/cdk';

const IMAGES_MAP = [
  {
    src: plantImage,
    alt: 'plant',
    width: 218,
    height: 255,
    style: { left: 0, bottom: '120px' },
    delay: 0.8,
  },
  {
    src: iphoneImage,
    alt: 'iphone',
    width: 192,
    height: 200,
    style: { left: '180px', bottom: '-10px' },
    delay: 0.9,
  },
  {
    src: coffeeImage,
    alt: 'coffee mug',
    width: 166,
    height: 169,
    style: { right: '350px', bottom: '-10px' },
    delay: 1,
  },
  {
    src: laptopImage,
    alt: 'laptop',
    width: 433,
    height: 532,
    style: { right: '-80px', bottom: '-50px' },
    delay: 0.7,
  },
];

const Title = styled(MainHeader)`
  white-space: normal;
  text-wrap: balance;
  ${MEDIA_QUERIES.tabletAndDesktop`
    white-space: pre;
    text-wrap: balance;
  `}
`;

const SubtitleWrapper = styled.div`
  display: block;
  marigin-inline: auto;
  width: min(100%, 800px);
  margin-top: 20px;
  margin-bottom: 40px;
`;

const CTA = styled(JoinCTA)`
  &&& {
    display: block;
    padding: 18px 44px;
    font-size: 1.8rem;
  }
`;

const ImagesContainer = styled.div`
  position: absolute;
  bottom: -140px;
  width: 1400px;
  height: 400px;
  display: none;
  z-index: 0;
  pointer-events: none;

  ${MEDIA_QUERIES.desktop`
    display: block
  `}
`;

const GithubLink = styled.a`
  ${font('main', { fontSize: '1.6rem' })}
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 20px;
  posotion: relative;
  z-index: 1;
`;

const MotionImage = styled(motion.create(Image))`
  position: absolute;
`;

const Images: React.FC = () => {
  return (
    <ImagesContainer>
      {IMAGES_MAP.map(({ src, alt, width, height, style, delay }, index) => (
        <MotionImage
          animate={{
            opacity: [0, 1],
            y: [30, 0],
            rotate: [index <= 2 ? 10 : -10, 0],
          }}
          transition={{
            delay,
            duration: 0.5,
          }}
          key={alt}
          src={src}
          alt={alt}
          width={width}
          height={height}
          style={{ ...style }}
        />
      ))}
    </ImagesContainer>
  );
};

export interface HeroProps {
  title: string;
  subtitle: string;
  ctaLabel: string;
  showGithubLink?: boolean;
}
export const Hero: React.FC<HeroProps> = ({ title, subtitle, ctaLabel, showGithubLink = false }) => {
  const onScrollToPolicySampleSection = () => {
    const policySampleSection = document.getElementById('policy-sample');
    if (policySampleSection) {
      policySampleSection.scrollIntoView({ behavior: 'smooth' });
    }
  };
  return (
    <Section py={[100, 140]} position="relative">
      <Image
        src={wave}
        alt="wave"
        priority
        style={{
          position: 'absolute',
          bottom: '0',
          width: 'max(100%, 1000px)',
          height: 'auto',
          pointerEvents: 'none',
        }}
      />
      <Images />

      <Title tabIndex={0}>{parse(title)}</Title>
      <SubtitleWrapper>
        <SectionParagraph tabIndex={0}>{parse(subtitle)}</SectionParagraph>
      </SubtitleWrapper>

      <CTA
        flavor="primary"
        glowing
        aria-label={ctaLabel}
        data-source="top"
        customUrl="#"
        onClick={onScrollToPolicySampleSection}
      >
        {ctaLabel}
      </CTA>

      {showGithubLink && (
        <GithubLink href="https://github.com/lemonade-hq/policy-2.0" target="_blank" referrerPolicy="no-referrer">
          <Image src={githubLogo} alt="github logo" height={18} width={18} />
          <span>Contribute on Github</span>
        </GithubLink>
      )}
    </Section>
  );
};
